"use client"

import React, { useState, useEffect } from "react"
import axios from "axios"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog"
import { Button } from "../ui/button"
import { Badge } from "../ui/badge"
import { Textarea } from "../ui/textarea"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table"
import { HoverCard, HoverCardTrigger, HoverCardContent } from "../ui/hover-card"
import { MessageSquare, X, Check, CheckCircle, XCircle, ChevronDown, ChevronRight } from 'lucide-react'
import { toast } from "../hooks/use-toast"

// Interfaces for API data
interface TimesheetDayEntry {
  day: string
  date: string
  total: string
  entries: TimesheetEntry[]
}

interface TimesheetEntry {
  engagement: string
  task: string
  comment: string
  hours: number
  minutes: number
  date: string // Original date string from API
  formattedDate?: string // Formatted date for display
}

// New interfaces for Engagement Hours Breakdown
interface EngagementTaskEntry {
  engagement: string
  task: string
  dailyHours: {
    [dateKey: string]: {
      hours: number
      comment: string | null
    }
  }
  totalHours: number
}

interface EngagementGroup {
  engagement: string
  tasks: EngagementTaskEntry[]
  totalHours: number
  percentage: number
}

interface ApiTimesheetData {
  data?: {
    timesheetID: number
    userID: number
    startDate: string
    endDate: string
    status: number
    hoursTotal: number
    minutesTotal: number
    timesheetLines?: {
      day: string
      date: string
      engagement: string
      task: string
      comment: string
      hours: number
      minutes: number
    }[]
    displayTitle: string
    approvedBy?: string
    approvedOn?: string
    approvalComment?: string
    rejectedBy?: string
    rejectedOn?: string
    rejectionComment?: string
  }
}

// Alternate shape: endpoint returns an array of line items directly
interface ApiTimesheetLineItem {
  lineID: number
  timesheetID: number
  engagementID: number
  engagementName: string
  taskID: number
  taskName: string
  hours: number
  minutes: number
  date: string
  comment: string | null
}

interface TimesheetReviewModalProps {
  isOpen: boolean
  onClose: () => void
  timesheet: any
  isViewOnly?: boolean // New prop to control button visibility
  onActionComplete?: (result: "approved" | "rejected", id: number) => void
}

export function TimesheetReviewModal({ isOpen, onClose, timesheet, isViewOnly = false, onActionComplete }: TimesheetReviewModalProps) {
  const [comment, setComment] = useState("")
  
  // API state management
  const [timesheetData, setTimesheetData] = useState<ApiTimesheetData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [mockDays, setMockDays] = useState<TimesheetDayEntry[]>([])
  const [actionLoading, setActionLoading] = useState<boolean>(false)
  const [engagementBreakdown, setEngagementBreakdown] = useState<EngagementGroup[]>([])
  const [weekDates, setWeekDates] = useState<{ date: string; label: string; dayName: string; dateKey: string }[]>([])
  const [expandedEngagements, setExpandedEngagements] = useState<Set<string>>(new Set())

  // API function to fetch timesheet data
  const fetchTimesheetData = async (timesheetId: number) => {
    setLoading(true)
    setError(null)
    try {
      const response = await axios.get(process.env.REACT_APP_API_URL + `/api/timesheet/${timesheetId}`, { withCredentials: true })
      ////console.log("timesheet id", timesheetId)
      setTimesheetData(response.data)

      // Two supported shapes: wrapped object with data.timesheetLines OR a flat array of line items
      const body = response.data
      let transformedDays: TimesheetDayEntry[] = []
      
      if (body?.data?.timesheetLines && Array.isArray(body.data.timesheetLines)) {
        transformedDays = transformApiDataToDays(body.data)
        setMockDays(transformedDays)
      } else if (Array.isArray(body) && body.length > 0) {
        transformedDays = transformFlatLinesToDays(body as ApiTimesheetLineItem[])
        setMockDays(transformedDays)
      } else {
        // If API did not return any lines, avoid forcing mock fixed dates
        setMockDays([])
      }

      // Transform to engagement breakdown format
      if (transformedDays.length > 0) {
        const startDate = body?.data?.startDate || body?.startDate
        const endDate = body?.data?.endDate || body?.endDate
        const { breakdown, weekDates } = transformToEngagementBreakdown(transformedDays, startDate, endDate)
        setEngagementBreakdown(breakdown)
        setWeekDates(weekDates)
        // Expand all engagements by default
        const allEngagements = new Set(breakdown.map(group => group.engagement))
        setExpandedEngagements(allEngagements)
      } else {
        setEngagementBreakdown([])
        setWeekDates([])
        setExpandedEngagements(new Set())
      }
    } catch (error: any) {
      console.error("Error fetching timesheet data:", error)
      setError("Failed to load timesheet data")
      // Keep prior mockDays; don't overwrite with fixed Dec week to avoid wrong display
    } finally {
      setLoading(false)
    }
  }

  // Transform API data (wrapped shape) to the expected format
  const transformApiDataToDays = (data: any): TimesheetDayEntry[] => {
    const daysMap = new Map<string, TimesheetDayEntry>()
    
    if (data.timesheetLines && Array.isArray(data.timesheetLines)) {
      data.timesheetLines.forEach((entry: any) => {
        const dayKey = entry.day.toLowerCase()
        // Try to get original date string - might be in different fields
        const originalDate = entry.originalDate || entry.dateString || entry.date
        
        if (!daysMap.has(dayKey)) {
          daysMap.set(dayKey, {
            day: entry.day,
            date: entry.date, // Formatted date for display
            total: "0h 0m",
            entries: []
          })
        }
        
        const dayEntry = daysMap.get(dayKey)!
        dayEntry.entries.push({
          engagement: entry.engagement,
          task: entry.task,
          comment: entry.comment || '',
          hours: entry.hours,
          minutes: entry.minutes,
          date: originalDate, // Store original date string for matching
          formattedDate: entry.date
        })
        
        // Calculate total hours for the day
        const totalMinutes = dayEntry.entries.reduce((total, e) => total + (e.hours * 60) + e.minutes, 0)
        const hours = Math.floor(totalMinutes / 60)
        const minutes = totalMinutes % 60
        dayEntry.total = `${hours}h ${minutes}m`
      })
    }
    // Sort Monday->Sunday, always include all days
    const order = ["monday","tuesday","wednesday","thursday","friday","saturday","sunday"]
    const values = Array.from(daysMap.values())
    // Always show all days, including Saturday and Sunday even if they have no entries
    values.sort((a,b) => order.indexOf(a.day.toLowerCase()) - order.indexOf(b.day.toLowerCase()))
    return values
  }

  // Transform flat array of line items to grouped days
  const transformFlatLinesToDays = (lines: ApiTimesheetLineItem[]): TimesheetDayEntry[] => {
    const daysMap = new Map<string, TimesheetDayEntry>()

    lines.forEach((entry) => {
      const jsDate = new Date(entry.date)
      const dayLabel = jsDate.toLocaleDateString(undefined, { weekday: 'long' })
      const dateLabel = jsDate.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
      const dayKey = dayLabel.toLowerCase()

      if (!daysMap.has(dayKey)) {
        daysMap.set(dayKey, {
          day: dayLabel,
          date: dateLabel,
          total: '0h 0m',
          entries: []
        })
      }

      const dayEntry = daysMap.get(dayKey)!
      dayEntry.entries.push({
        engagement: entry.engagementName,
        task: entry.taskName,
        comment: entry.comment || '',
        hours: entry.hours,
        minutes: entry.minutes,
        date: entry.date, // Original date string from API
        formattedDate: dateLabel
      })

      const totalMinutes = dayEntry.entries.reduce((sum, e) => sum + e.hours * 60 + e.minutes, 0)
      const hours = Math.floor(totalMinutes / 60)
      const minutes = totalMinutes % 60
      dayEntry.total = `${hours}h ${minutes}m`
    })
    // Sort Monday->Sunday, always include all days
    const order = ["monday","tuesday","wednesday","thursday","friday","saturday","sunday"]
    const values = Array.from(daysMap.values())
    // Always show all days, including Saturday and Sunday even if they have no entries
    values.sort((a,b) => order.indexOf(a.day.toLowerCase()) - order.indexOf(b.day.toLowerCase()))
    return values
  }

  // Transform data to Engagement Hours Breakdown format
  const transformToEngagementBreakdown = (days: TimesheetDayEntry[], startDate?: string, endDate?: string): { breakdown: EngagementGroup[], weekDates: { date: string; label: string; dayName: string; dateKey: string }[] } => {
    const engagementMap = new Map<string, Map<string, EngagementTaskEntry>>()
    const dateMap = new Map<string, { date: string; label: string; dayName: string }>()
    
    // Collect all dates and group by engagement/task
    days.forEach((dayEntry) => {
      // Use the original date string from entries as the key for matching
      const dateKey = dayEntry.entries.length > 0 ? dayEntry.entries[0].date : dayEntry.date
      
      // Create date info for display
      let displayDate: Date
      try {
        displayDate = new Date(dateKey)
        if (isNaN(displayDate.getTime())) {
          displayDate = new Date(dayEntry.date)
        }
      } catch {
        displayDate = new Date(dayEntry.date)
      }
      
      const dayName = displayDate.toLocaleDateString(undefined, { weekday: 'short' }).toUpperCase().substring(0, 3)
      const day = displayDate.getDate()
      
      if (!dateMap.has(dateKey)) {
        dateMap.set(dateKey, {
          date: dayEntry.date, // Formatted date for display
          label: `${dayName} ${day}`,
          dayName: dayName
        })
      }

      dayEntry.entries.forEach((entry) => {
        const engagement = entry.engagement
        const task = entry.task
        const totalHours = entry.hours + (entry.minutes / 60)
        const entryDateKey = entry.date || dateKey // Use entry's date or fallback to day's date

        if (!engagementMap.has(engagement)) {
          engagementMap.set(engagement, new Map())
        }

        const taskMap = engagementMap.get(engagement)!
        if (!taskMap.has(task)) {
          taskMap.set(task, {
            engagement,
            task,
            dailyHours: {},
            totalHours: 0
          })
        }

        const taskEntry = taskMap.get(task)!
        if (!taskEntry.dailyHours[entryDateKey]) {
          taskEntry.dailyHours[entryDateKey] = {
            hours: 0,
            comment: null
          }
        }
        taskEntry.dailyHours[entryDateKey].hours += totalHours
        if (entry.comment && entry.comment.trim()) {
          // Combine comments if multiple entries for same day
          if (taskEntry.dailyHours[entryDateKey].comment) {
            taskEntry.dailyHours[entryDateKey].comment += '\n' + entry.comment
          } else {
            taskEntry.dailyHours[entryDateKey].comment = entry.comment
          }
        }
        taskEntry.totalHours += totalHours
      })
    })

    // Convert to array format and calculate totals
    const breakdown: EngagementGroup[] = []
    let grandTotalHours = 0

    engagementMap.forEach((taskMap, engagement) => {
      const tasks: EngagementTaskEntry[] = Array.from(taskMap.values())
      const engagementTotal = tasks.reduce((sum, t) => sum + t.totalHours, 0)
      grandTotalHours += engagementTotal

      breakdown.push({
        engagement,
        tasks,
        totalHours: engagementTotal,
        percentage: 0 // Will calculate after we have grand total
      })
    })

    // Calculate percentages
    breakdown.forEach((group) => {
      group.percentage = grandTotalHours > 0 ? (group.totalHours / grandTotalHours) * 100 : 0
    })

    // Extract and sort week dates (Monday to Sunday)
    // Always generate all 7 days (Mon-Sun) regardless of data
    let weekDates: { date: string; label: string; dayName: string; dateKey: string }[] = []
    
    // Determine the Monday of the week
    let monday: Date
    
    if (startDate) {
      // Use provided start date, but ensure we start from Monday
      const start = new Date(startDate)
      const dayOfWeek = start.getDay()
      const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek // If Sunday, go back 6 days, otherwise go to Monday
      monday = new Date(start)
      monday.setDate(start.getDate() + mondayOffset)
    } else {
      // Fallback: infer from data
      const sortedDateKeys = Array.from(dateMap.keys()).sort((a, b) => {
        const dateA = new Date(a)
        const dateB = new Date(b)
        return dateA.getTime() - dateB.getTime()
      })
      
      if (sortedDateKeys.length > 0) {
        // Get the first date to determine the week
        const firstDate = new Date(sortedDateKeys[0])
        const dayOfWeek = firstDate.getDay()
        const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek
        monday = new Date(firstDate)
        monday.setDate(firstDate.getDate() + mondayOffset)
      } else {
        // Last fallback: use current week's Monday
        const today = new Date()
        const dayOfWeek = today.getDay()
        const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek
        monday = new Date(today)
        monday.setDate(today.getDate() + mondayOffset)
      }
    }
    
    // Always generate all 7 days (Mon-Sun) starting from Monday
    const sortedDateKeys = Array.from(dateMap.keys())
    for (let i = 0; i < 7; i++) {
      const currentDate = new Date(monday)
      currentDate.setDate(monday.getDate() + i)
      
      const dayName = currentDate.toLocaleDateString(undefined, { weekday: 'short' }).toUpperCase().substring(0, 3)
      const day = currentDate.getDate()
      const formattedDate = currentDate.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
      
      // Try to find matching date key from our data, or use ISO string
      const dateKey = sortedDateKeys.find(key => {
        try {
          const keyDate = new Date(key)
          return keyDate.toDateString() === currentDate.toDateString()
        } catch {
          return false
        }
      }) || currentDate.toISOString().split('T')[0]
      
      weekDates.push({
        date: formattedDate,
        label: `${dayName} ${day}`,
        dayName: dayName,
        dateKey: dateKey
      })
    }
    
    // Ensure we have exactly 7 days
    if (weekDates.length !== 7) {
      console.warn(`Expected 7 days but got ${weekDates.length}. Regenerating...`)
      weekDates = []
      for (let i = 0; i < 7; i++) {
        const currentDate = new Date(monday)
        currentDate.setDate(monday.getDate() + i)
        const dayName = currentDate.toLocaleDateString(undefined, { weekday: 'short' }).toUpperCase().substring(0, 3)
        const day = currentDate.getDate()
        const formattedDate = currentDate.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
        weekDates.push({
          date: formattedDate,
          label: `${dayName} ${day}`,
          dayName: dayName,
          dateKey: currentDate.toISOString().split('T')[0]
        })
      }
    }

    return { breakdown, weekDates }
  }

  // removed mock fallback data

  // Fetch timesheet data when modal opens
  useEffect(() => {
    if (isOpen && timesheet?.id) {
      // reset comment for each timesheet open
      setComment("")
      fetchTimesheetData(timesheet.id)
    }
  }, [isOpen, timesheet?.id])

  ////console.log("timesheet.tsstatus [inside tsreviewmodal func]:", timesheet?.tsstatus);
  
  // Get status from timesheetData if available, otherwise use timesheet prop
  const getStatusFromData = () => {
    if (timesheetData?.data?.status !== undefined) {
      return timesheetData.data.status
    }
    return timesheet?.tsstatus
  }
  
  const currentStatus = getStatusFromData()
  
  // Debug logging
  ////console.log("Modal timesheet prop:", timesheet)
  ////console.log("Modal timesheetData:", timesheetData)
  
  const TsApprovalStatus = {
    status: currentStatus === "approved" || currentStatus === 2
      ? "Approved"
      : currentStatus === "rejected" || currentStatus === 3
        ? "Rejected"
        : "Pending",

    approvedBy: timesheetData?.data?.approvedBy || "John Doe (Manager)",

    approvedOn: timesheetData?.data?.approvedOn ? new Date(timesheetData.data.approvedOn).toLocaleDateString() : "2025-07-20",

    comment: currentStatus === "rejected" || currentStatus === 3
      ? timesheetData?.data?.rejectionComment || "Timesheet rejected by manager."
      : timesheetData?.data?.approvalComment || "Timesheet approved by manager."
  }


  const getTsApprovalBadge = (status: string) => {
    switch (status) {
      case "Approved":
        return <Badge className="bg-green-100 text-green-700 hover:bg-green-100 px-3 py-1 text-sm font-medium flex items-center gap-1"><CheckCircle className="h-3 w-3" /> Approved</Badge>
      case "Rejected":
        return <Badge className="bg-red-100 text-red-700 hover:bg-red-100 px-3 py-1 text-sm font-medium flex items-center gap-1"><CheckCircle className="h-3 w-3" /> Rejected</Badge>
      default:
        return null
    }
  }

  const toggleEngagement = (engagement: string) => {
    setExpandedEngagements(prev => {
      const newSet = new Set(prev)
      if (newSet.has(engagement)) {
        newSet.delete(engagement)
      } else {
        newSet.add(engagement)
      }
      return newSet
    })
  }

  // Convert decimal hours to hours and minutes format (e.g., 5.5 -> "5h 30m")
  const formatHoursMinutes = (decimalHours: number): string => {
    if (decimalHours === 0) return '-'
    const totalMinutes = Math.round(decimalHours * 60)
    const hours = Math.floor(totalMinutes / 60)
    const minutes = totalMinutes % 60
    if (hours === 0 && minutes === 0) return '-'
    if (hours === 0) return `${minutes}m`
    if (minutes === 0) return `${hours}h`
    return `${hours}h ${minutes}m`
  }

  // Calculate daily totals for an engagement (aggregated across all tasks)
  const getEngagementDailyTotals = (group: EngagementGroup, weekDates: { dateKey: string }[]) => {
    const dailyTotals: { [dateKey: string]: number } = {}
    
    weekDates.forEach((dateInfo) => {
      let totalHours = 0
      group.tasks.forEach((task) => {
        const dayData = task.dailyHours[dateInfo.dateKey]
        if (dayData) {
          totalHours += dayData.hours
        }
      })
      dailyTotals[dateInfo.dateKey] = totalHours
    })
    
    return dailyTotals
  }

  // Check if a date is Saturday or Sunday
  const isWeekend = (dateKey: string, dayName?: string): boolean => {
    // Use dayName if available (more reliable)
    if (dayName) {
      const dayUpper = dayName.toUpperCase()
      return dayUpper === 'SAT' || dayUpper === 'SUN'
    }
    // Fallback to parsing dateKey
    try {
      const date = new Date(dateKey)
      const dayOfWeek = date.getDay()
      return dayOfWeek === 0 || dayOfWeek === 6 // 0 = Sunday, 6 = Saturday
    } catch {
      return false
    }
  }

  // Check if a date has any entries across all engagements
  const hasEntriesForDate = (dateKey: string): boolean => {
    return engagementBreakdown.some(group => 
      group.tasks.some(task => {
        const dayData = task.dailyHours[dateKey]
        return dayData && dayData.hours > 0
      })
    )
  }

  const handleApprove = async () => {
    if (!timesheet?.id || actionLoading) return
    setActionLoading(true)
    try {
      await axios.post(
        process.env.REACT_APP_API_URL + `/api/timesheet/Approve`,
        { timesheetID: timesheet.id, approvalComment: comment || "", modUser: 0 },
        { withCredentials: true }
      )
      toast({ title: "Timesheet approved successfully", className: "border-green-500" })
      if (timesheet?.id) onActionComplete?.("approved", timesheet.id)
      onClose()
    } catch (err) {
      console.error("Approve failed", err)
      setError("Failed to approve timesheet")
    } finally {
      // clear comment after action to avoid persistence between items
      setComment("")
      setActionLoading(false)
    }
  }

  const handleReject = async () => {
    if (!timesheet?.id || actionLoading) return
    setActionLoading(true)
    try {
      await axios.post(
        process.env.REACT_APP_API_URL + `/api/timesheet/Reject`,
        { timesheetID: timesheet.id, rejectionComment: comment || "", modUser: 0 },
        { withCredentials: true }
      )
      toast({ title: "Timesheet rejected successfully", className: "border-red-500" })
      if (timesheet?.id) onActionComplete?.("rejected", timesheet.id)
      onClose()
    } catch (err) {
      console.error("Reject failed", err)
      setError("Failed to reject timesheet")
    } finally {
      // clear comment after action to avoid persistence between items
      setComment("")
      setActionLoading(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent style={{ maxWidth: '1200px' }} className="w-full max-h-[90vh] flex flex-col bg-white border border-gray-200 rounded-lg shadow-lg p-6">
      {/* Dialog Title for accessibility - visually hidden */}
      <DialogHeader className="sr-only">
        <DialogTitle>Timesheet Review - {timesheet?.name || "Unknown User"}</DialogTitle>
      </DialogHeader>
      
      {/* Header - Compact */}
        <div className="border-b border-gray-200 p-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 flex-wrap min-w-0">
              <h2 className="text-xl font-semibold text-gray-900">
                {timesheet?.name || "Unknown User"}
              </h2>
              <Badge variant="outline" className="text-blue-600 border-blue-200 bg-blue-50 px-3 py-1 text-sm font-medium">
                {timesheet?.duration || timesheetData?.data?.displayTitle || "Unknown Duration"}
              </Badge>
              <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100 px-3 py-1 text-sm font-medium">
                {timesheet?.time || (timesheetData?.data ? `${timesheetData.data.hoursTotal}h ${timesheetData.data.minutesTotal}m` : "0h 0m")}
              </Badge>
              {getTsApprovalBadge(TsApprovalStatus.status)}
            </div>
          </div>
        </div>

 {/* Content - Scrollable */}
         <div className="flex-1 overflow-y-auto overflow-x-hidden px-6 py-4 space-y-6">
           {/* Loading State */}
           {loading && (
             <div className="flex items-center justify-center py-8">
              <div className="text-gray-500">Loading timesheet data...</div>
            </div>
          )}

           {/* Error State */}
           {error && !loading && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="text-red-700">{error}</div>
              <Button 
                size="sm" 
                variant="outline" 
                onClick={() => timesheet?.id && fetchTimesheetData(timesheet.id)}
                className="mt-2 text-red-600 border-red-300 hover:bg-red-50"
              >
                Retry
              </Button>
            </div>
          )}

           {/* Empty State */}
           {!loading && !error && mockDays.length === 0 && (
            <div className="border border-gray-200 rounded-lg bg-gray-50 p-6 text-center text-gray-700">
              No data found for this timesheet.
            </div>
          )}

          {/* Engagement Hours Breakdown */}
          {!loading && !error && engagementBreakdown.length > 0 && weekDates.length > 0 && (
            <div className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900">Engagement Hours Breakdown</h2>
              
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-blue-600 text-white">
                        <TableHead className="text-white font-semibold p-3 text-sm">ENGAGEMENT / TASK</TableHead>
                        {weekDates.map((dateInfo, idx) => {
                          const isWeekendDay = isWeekend(dateInfo.dateKey, dateInfo.dayName)
                          const hasEntries = hasEntriesForDate(dateInfo.dateKey)
                          const shouldFade = isWeekendDay && !hasEntries
                          return (
                            <TableHead 
                              key={idx} 
                              className={`text-white font-semibold p-3 text-sm text-center ${shouldFade ? 'opacity-50' : ''}`}
                            >
                              {dateInfo.label}
                            </TableHead>
                          )
                        })}
                        <TableHead className="text-white font-semibold p-3 text-sm text-center">TOTAL</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {engagementBreakdown.map((group, groupIdx) => {
                        const isExpanded = expandedEngagements.has(group.engagement)
                        const hasTasks = group.tasks.length > 0
                        const engagementDailyTotals = getEngagementDailyTotals(group, weekDates)
                        
                        return (
                          <React.Fragment key={groupIdx}>
                            {/* Engagement Header Row */}
                            <TableRow 
                              className="bg-gray-50 cursor-pointer hover:bg-gray-100"
                              onClick={() => toggleEngagement(group.engagement)}
                            >
                              <TableCell className="font-bold text-gray-900 p-3 text-sm">
                                <div className="flex items-center gap-2">
                                  {isExpanded ? (
                                    <ChevronDown className="h-4 w-4 text-gray-600" />
                                  ) : (
                                    <ChevronRight className="h-4 w-4 text-gray-600" />
                                  )}
                                  <span>{group.engagement}</span>
                                </div>
                              </TableCell>
                              {/* Show daily totals when collapsed, empty cells when expanded */}
                              {!isExpanded ? (
                                weekDates.map((dateInfo, dateIdx) => {
                                  const dailyTotal = engagementDailyTotals[dateInfo.dateKey] || 0
                                  const isWeekendDay = isWeekend(dateInfo.dateKey, dateInfo.dayName)
                                  const hasEntries = hasEntriesForDate(dateInfo.dateKey)
                                  const shouldFade = isWeekendDay && !hasEntries
                                  return (
                                    <TableCell 
                                      key={dateIdx} 
                                      className={`text-center p-3 text-sm ${shouldFade ? 'opacity-50' : ''}`}
                                    >
                                      <span className="text-gray-900">
                                        {formatHoursMinutes(dailyTotal)}
                                      </span>
                                    </TableCell>
                                  )
                                })
                              ) : (
                                weekDates.map((dateInfo, dateIdx) => {
                                  const isWeekendDay = isWeekend(dateInfo.dateKey, dateInfo.dayName)
                                  const hasEntries = hasEntriesForDate(dateInfo.dateKey)
                                  const shouldFade = isWeekendDay && !hasEntries
                                  return (
                                    <TableCell 
                                      key={dateIdx} 
                                      className={`p-3 text-sm ${shouldFade ? 'opacity-50' : ''}`}
                                    ></TableCell>
                                  )
                                })
                              )}
                              <TableCell className="text-center p-3 text-sm">
                                {hasTasks && (
                                  <div className="flex flex-col items-center">
                                    <span className="text-blue-600 font-semibold">{formatHoursMinutes(group.totalHours)}</span>
                                    <span className="text-gray-500 text-xs">
                                      {group.percentage % 1 === 0 ? group.percentage.toFixed(0) : group.percentage.toFixed(2)}%
                                    </span>
                                  </div>
                                )}
                              </TableCell>
                            </TableRow>
                            
                            {/* Task Rows - Only show when expanded */}
                            {isExpanded && group.tasks.map((task, taskIdx) => (
                              <TableRow key={taskIdx} className="hover:bg-gray-50">
                                <TableCell className="p-3 text-sm text-gray-700 pl-12">
                                  {task.task}
                                </TableCell>
                                {weekDates.map((dateInfo, dateIdx) => {
                                  const dayData = task.dailyHours[dateInfo.dateKey]
                                  const hours = dayData?.hours || 0
                                  const hasComment = dayData?.comment && dayData.comment.trim() !== ''
                                  const isWeekendDay = isWeekend(dateInfo.dateKey, dateInfo.dayName)
                                  const hasEntries = hasEntriesForDate(dateInfo.dateKey)
                                  const shouldFade = isWeekendDay && !hasEntries
                                  
                                  return (
                                    <TableCell 
                                      key={dateIdx} 
                                      className={`text-center p-3 text-sm ${shouldFade ? 'opacity-50' : ''}`}
                                    >
                                      <div className="flex items-center justify-center gap-1">
                                        <span className="text-gray-900">
                                          {formatHoursMinutes(hours)}
                                        </span>
                                        {hasComment && (
                                          <HoverCard openDelay={100} closeDelay={200}>
                                            <HoverCardTrigger asChild>
                                              <button className="text-blue-600 hover:text-blue-800 cursor-pointer">
                                                <MessageSquare className="h-4 w-4" />
                                              </button>
                                            </HoverCardTrigger>
                                            <HoverCardContent 
                                              className="max-w-xs p-3"
                                              side="top"
                                              onPointerDownOutside={(e) => {
                                                // Prevent closing when clicking inside the scrollable area
                                                const target = e.target as HTMLElement
                                                if (target.closest('[data-slot="hover-card-content"]')) {
                                                  e.preventDefault()
                                                }
                                              }}
                                            >
                                              <div className="space-y-2">
                                                <h4 className="font-semibold text-sm">Comment</h4>
                                                <div 
                                                  className="overflow-y-auto max-h-48 pr-1"
                                                  style={{ 
                                                    overscrollBehavior: 'contain',
                                                    WebkitOverflowScrolling: 'touch',
                                                    scrollbarWidth: 'thin'
                                                  }}
                                                >
                                                  <p className="whitespace-pre-wrap text-sm break-words">
                                                    {dayData.comment}
                                                  </p>
                                                </div>
                                              </div>
                                            </HoverCardContent>
                                          </HoverCard>
                                        )}
                                      </div>
                                    </TableCell>
                                  )
                                })}
                                <TableCell className="text-center p-3 text-sm">
                                  <span className="text-blue-600">{formatHoursMinutes(task.totalHours)}</span>
                                </TableCell>
                              </TableRow>
                            ))}
                          </React.Fragment>
                        )
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          )}
          </div>

            {/* Footer  */}
         <div className="border-t border-gray-200 bg-gray-50 p-4">
           <div className="flex gap-3 items-end">   
{/* Comments Field */}
{!isViewOnly && (
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs font-medium text-gray-700">Comments</label>
                <span className="text-xs text-gray-500">
                  {comment.length}/1000
                </span>
              </div>
              <Textarea
                placeholder="Enter your comments here..."
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                disabled={TsApprovalStatus.status !== "Pending"}
                className="min-h-[50px] bg-white border-gray-200 text-gray-900 placeholder:text-gray-500 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed text-sm resize-none"
                maxLength={1000}
              />
            </div>
          )}
                 {/* Action Buttons */}
           <div className="flex gap-2 justify-end">
             {isViewOnly ? (
              <Button onClick={onClose} className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 text-base font-medium rounded-md h-fit">
                Close
              </Button>
            ) : (
              <>
                <Button onClick={handleApprove}
                  disabled={
                    !(
                      TsApprovalStatus.status === "Pending"
                    ) || actionLoading
                  }
                  className="flex items-center gap-1.5 bg-green-600 hover:bg-green-700 text-white px-6 py-2.5 text-base font-medium rounded-md h-fit">
                  <Check className="h-4 w-4" />
                  Approve
                </Button>
                <Button onClick={handleReject}
                  disabled={
                    !(
                      TsApprovalStatus.status === "Pending"
                    ) || actionLoading
                  }
                  className="flex items-center gap-1.5 bg-red-600 hover:bg-red-700 text-white px-6 py-2.5 text-base font-medium rounded-md h-fit">
                  <X className="h-4 w-4" />
                  Reject
                </Button>
              </>
            )}
          </div>
        </div>
        </div>
      </DialogContent>

    </Dialog>
  )
}